import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  CreatedInvitationResponse,
  DeletedInvitationsResponse,
  InvitationView,
  PageResponse,
  PermissionBundle,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import {
  canDeleteInvitation,
  canWithdrawInvitation,
  createdInvitationNotice,
  invitationDeletionBatches,
  invitationStatusLabelKeys,
  staffInvitationBody,
  type CreatedInvitationNotice,
} from './invitations';
import { InvitationLinkNotice } from './InvitationLinkNotice';
import { assignableBundles, bundleLabelKeys } from './roles';

export interface InvitationsPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
  /** Staff roles can be offered only by someone who may manage roles; others see the list only. */
  canOfferStaffRoles: boolean;
}

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

export function InvitationsPanel({
  locale,
  organizationId,
  csrfToken,
  canOfferStaffRoles,
}: InvitationsPanelProps): React.JSX.Element {
  const [invitations, setInvitations] = useState<InvitationView[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [ticked, setTicked] = useState<ReadonlySet<PermissionBundle>>(new Set(['coach']));
  const [created, setCreated] = useState<{
    notice: CreatedInvitationNotice;
    bundles: PermissionBundle[];
  } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [confirmingDeletion, setConfirmingDeletion] = useState(false);
  const [deletedCount, setDeletedCount] = useState<number | null>(null);
  const base = `/api/v1/organizations/${organizationId}/invitations`;

  const load = useCallback(
    async (cursor: string | null) => {
      try {
        const query = cursor === null ? '' : `?cursor=${encodeURIComponent(cursor)}`;
        const page = await apiRequest<PageResponse<InvitationView>>(`${base}${query}`);
        setInvitations((current) =>
          cursor === null ? page.items : [...(current ?? []), ...page.items],
        );
        setNextCursor(page.nextCursor);
      } catch (error) {
        setErrorKey(errorKeyOf(error));
      }
    },
    [base],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  const body = staffInvitationBody(recipientEmail, ticked, assignableBundles);
  const deletable = (invitations ?? []).filter((invitation) =>
    canDeleteInvitation(invitation, canOfferStaffRoles),
  );
  // Only rows still in the list count, so a selection never outlives a reload.
  const selectedIds = deletable
    .filter((invitation) => selected.has(invitation.id))
    .map((invitation) => invitation.id);
  const allSelected = deletable.length > 0 && selectedIds.length === deletable.length;

  function toggle(bundle: PermissionBundle, checked: boolean): void {
    setTicked((current) => {
      const next = new Set(current);
      if (checked) next.add(bundle);
      else next.delete(bundle);
      return next;
    });
  }

  async function create(): Promise<void> {
    if (body === null) return;
    setErrorKey(null);
    setCreated(null);
    try {
      const response = await apiRequest<CreatedInvitationResponse>(base, {
        method: 'POST',
        body,
        csrfToken,
      });
      setCreated({
        notice: createdInvitationNotice(window.location.origin, response),
        bundles: response.permissionBundles,
      });
      setRecipientEmail('');
      await load(null);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  function roleList(bundles: readonly PermissionBundle[]): string {
    return bundles.map((bundle) => translate(locale, bundleLabelKeys[bundle])).join(', ');
  }

  function select(invitationId: string, checked: boolean): void {
    setConfirmingDeletion(false);
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(invitationId);
      else next.delete(invitationId);
      return next;
    });
  }

  function selectAll(checked: boolean): void {
    setConfirmingDeletion(false);
    setSelected(checked ? new Set(deletable.map((invitation) => invitation.id)) : new Set());
  }

  async function deleteSelected(): Promise<void> {
    setErrorKey(null);
    setConfirmingDeletion(false);
    setDeletedCount(null);
    let deleted = 0;
    try {
      for (const invitationIds of invitationDeletionBatches(selectedIds)) {
        const response = await apiRequest<DeletedInvitationsResponse>(`${base}/delete`, {
          method: 'POST',
          body: { invitationIds },
          csrfToken,
        });
        deleted += response.deletedCount;
      }
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
    if (deleted > 0) setDeletedCount(deleted);
    setSelected(new Set());
    await load(null);
  }

  async function revoke(invitation: InvitationView): Promise<void> {
    setErrorKey(null);
    setConfirming(null);
    try {
      await apiRequest(`${base}/${invitation.id}/revoke`, {
        method: 'POST',
        body: {},
        csrfToken,
        ifMatchVersion: invitation.version,
      });
      await load(null);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  return (
    <Box component="section" aria-labelledby="invitations-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="invitations-title" gutterBottom>
        {translate(locale, 'invitations.title')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {created !== null && (
        <InvitationLinkNotice locale={locale} notice={created.notice}>
          <Typography gutterBottom>
            {translate(locale, 'invitations.createdRoles')}:{' '}
            <strong>{roleList(created.bundles)}</strong>
          </Typography>
        </InvitationLinkNotice>
      )}
      {canOfferStaffRoles ? (
        <Stack
          component="form"
          spacing={2}
          sx={{ mb: 2, alignItems: 'flex-start' }}
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <TextField
            required
            size="small"
            type="email"
            label={translate(locale, 'invitations.create.email')}
            value={recipientEmail}
            onChange={(event) => {
              setRecipientEmail(event.target.value);
            }}
            sx={{ minWidth: { sm: 320 }, width: { xs: '100%', sm: 'auto' } }}
          />
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend">
              {translate(locale, 'invitations.create.bundles')}
            </FormLabel>
            <FormGroup row>
              {assignableBundles.map((option) => (
                <FormControlLabel
                  key={option}
                  label={translate(locale, bundleLabelKeys[option])}
                  control={
                    <Checkbox
                      checked={ticked.has(option)}
                      onChange={(event) => {
                        toggle(option, event.target.checked);
                      }}
                    />
                  }
                />
              ))}
            </FormGroup>
            <FormHelperText>{translate(locale, 'invitations.create.bundlesHint')}</FormHelperText>
          </FormControl>
          <Button type="submit" variant="contained" disabled={body === null}>
            {translate(locale, 'invitations.create.submit')}
          </Button>
        </Stack>
      ) : (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {translate(locale, 'invitations.familyNote')}
        </Typography>
      )}
      {invitations === null && errorKey === null && (
        <Typography>{translate(locale, 'invitations.loading')}</Typography>
      )}
      {invitations?.length === 0 && (
        <Typography>{translate(locale, 'invitations.empty')}</Typography>
      )}
      {deletedCount !== null && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => {
            setDeletedCount(null);
          }}
        >
          {translate(locale, 'invitations.deleted').replace('{count}', String(deletedCount))}
        </Alert>
      )}
      {deletable.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
          <FormControlLabel
            label={translate(locale, 'invitations.selectAll')}
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={selectedIds.length > 0 && !allSelected}
                onChange={(event) => {
                  selectAll(event.target.checked);
                }}
              />
            }
          />
          {confirmingDeletion ? (
            <>
              <Button
                size="small"
                color="error"
                variant="contained"
                onClick={() => {
                  void deleteSelected();
                }}
              >
                {translate(locale, 'invitations.delete.confirm').replace(
                  '{count}',
                  String(selectedIds.length),
                )}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setConfirmingDeletion(false);
                }}
              >
                {translate(locale, 'invitations.delete.cancel')}
              </Button>
            </>
          ) : (
            <Button
              size="small"
              color="error"
              disabled={selectedIds.length === 0}
              onClick={() => {
                setConfirmingDeletion(true);
              }}
            >
              {translate(locale, 'invitations.delete').replace(
                '{count}',
                String(selectedIds.length),
              )}
            </Button>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
            {translate(locale, 'invitations.delete.hint')}
          </Typography>
        </Box>
      )}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {invitations?.map((invitation) => (
          <Box
            component="li"
            key={invitation.id}
            sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
          >
            {deletable.length > 0 && (
              // Waiting rows keep an empty slot so the e-mail addresses stay aligned.
              <Box sx={{ width: 42, flexShrink: 0 }}>
                {canDeleteInvitation(invitation, canOfferStaffRoles) && (
                  <Checkbox
                    sx={{ padding: 1 }}
                    checked={selected.has(invitation.id)}
                    slotProps={{
                      input: {
                        'aria-label': translate(locale, 'invitations.select').replace(
                          '{email}',
                          invitation.recipientEmail,
                        ),
                      },
                    }}
                    onChange={(event) => {
                      select(invitation.id, event.target.checked);
                    }}
                  />
                )}
              </Box>
            )}
            <Typography component="span" sx={{ overflowWrap: 'anywhere' }}>
              {invitation.recipientEmail} · {roleList(invitation.permissionBundles)}
              {invitation.status === 'pending' &&
                ` · ${translate(locale, 'invitations.expires')} ${invitation.expiresAt
                  .slice(0, 16)
                  .replace('T', ' ')} UTC`}
            </Typography>
            <Chip
              size="small"
              label={translate(locale, invitationStatusLabelKeys[invitation.status])}
              color={invitation.status === 'pending' ? 'success' : 'default'}
            />
            {invitation.emailSentAt !== null && (
              <Chip
                size="small"
                variant="outlined"
                label={translate(locale, 'invitations.emailed')}
              />
            )}
            {canWithdrawInvitation(invitation) &&
              (confirming === invitation.id ? (
                <Button
                  size="small"
                  color="error"
                  variant="contained"
                  onClick={() => {
                    void revoke(invitation);
                  }}
                >
                  {translate(locale, 'invitations.revoke.confirm')}
                </Button>
              ) : (
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setConfirming(invitation.id);
                  }}
                >
                  {translate(locale, 'invitations.revoke')}
                </Button>
              ))}
          </Box>
        ))}
      </Stack>
      {nextCursor !== null && (
        <Button
          sx={{ mt: 2 }}
          onClick={() => {
            void load(nextCursor);
          }}
        >
          {translate(locale, 'invitations.loadMore')}
        </Button>
      )}
    </Box>
  );
}
